import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PermMediaIcon from '@mui/icons-material/PermMedia';
import SaveIcon from '@mui/icons-material/Save';
import { Alert, Box, Button, Chip, CircularProgress, Snackbar, Tooltip, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import videoCRUDServiceInstance from '../../services/VideoCRUDService';
import { useTrackUsage } from '../../hooks/useTrackUsage';
import { videoEditorHelpItems } from '../../utils/helpItems';
import HelpOverlay from '../common/HelpOverlay';
import { PageHeader } from '../common/PageHeader';
import FolderBreadcrumbs from '../navigation/ModernBreadcrumbs';
import AddDubSyncTrackDialog from './dubsync/AddDubSyncTrackDialog';
import { buildVttFromCues } from './dubsync/useDubSync';
import { EditorProvider, useEditor } from './EditorContext';
import { parseSrt, parseVtt } from './editorUtils';
import ExportDialog from './ExportDialog';
import MediaLibraryPanel from './MediaLibraryPanel';
import ProgramMonitor from './ProgramMonitor';
import TimelinePanel from './timeline/TimelinePanel';

/* ── Styles ────────────────────────────────────────────────────── */

const Root = styled.div`
    display: flex;
    flex-direction: column;
    height: 100vh;
    background: ${(p) => (p.$dark ? '#0d0d17' : '#f0f2f8')};
    color: ${(p) => (p.$dark ? '#fff' : '#111')};
    overflow: hidden;
    font-family: 'Roboto', sans-serif;
`;

/* Single merged top bar: breadcrumbs (left) | title (center) | actions (right) */
const TopBar = styled.div`
    display: flex;
    align-items: center;
    padding: 0 12px;
    background: ${(p) => (p.$dark ? '#161625' : '#e8eaf6')};
    border-bottom: 2px solid ${(p) => (p.$dark ? '#1565c0' : '#3f51b5')};
    flex-shrink: 0;
    height: 44px;
    box-sizing: border-box;
    gap: 8px;
`;

const TopLeft = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
`;

const TopCenter = styled.div`
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
`;

const TopRight = styled.div`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
    min-width: 0;
`;

const EditorBody = styled.div`
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

/* Top section: left media panel + right video panel side by side */
const TopArea = styled.div`
    display: flex;
    flex-direction: row;
    min-height: 0;
    overflow: hidden;
    flex-shrink: 0;
`;

/* Slim collapsible left panel for media library */
const LeftPanel = styled.div`
    width: ${(p) => (p.$open ? '200px' : '0')};
    flex-shrink: 0;
    overflow: hidden;
    border-right: 1px solid ${(p) => (p.$dark ? '#2a2a3a' : '#c8cce0')};
    transition: width 0.2s ease;
    display: flex;
    flex-direction: column;
`;

/* Video panel — fills remaining top space */
const VideoPanel = styled.div`
    flex: 1;
    min-width: 0;
    overflow: hidden;
`;

const DragHandle = styled.div`
    height: 5px;
    background: ${(p) => (p.$dark ? '#2a2a3a' : '#d0d4e8')};
    cursor: row-resize;
    flex-shrink: 0;
    transition: background 0.15s;
    &:hover { background: #1565c0; }
`;

const TimelinePane = styled.div`
    flex: 1;
    min-height: 0;
    overflow: hidden;
`;

/* ── Inner editor (needs EditorContext) ─────────────────────────── */

function EditorInner({ darkMode, toggleDarkMode, themeVariant }) {
    useTrackUsage('VideoEditorPage', 'editor', 'videoeditor');
    const { state, setVideo, loadSubtitles, toggleMediaLibrary, restoreEditorState, markSaved, addDubSyncTrack, updateDubSyncTrack, addAudioTrack, updateDubSyncCues, setDubSyncCues, pendingDubSyncEditsRef } = useEditor();
    const { videoId, videoTitle } = state;
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();

    const [loading, setLoading] = useState(true);
    const [exportOpen, setExportOpen] = useState(false);
    const [error, setError] = useState(null);
    const [saveStatus, setSaveStatus] = useState(null); // null | 'saving' | 'saved' | 'error'
    const [lastSavedAt, setLastSavedAt] = useState(null);
    const [dubSyncDialogOpen, setDubSyncDialogOpen] = useState(false);
    const [currentVideoData, setCurrentVideoData] = useState(null);
    // Breadcrumb path — read from localStorage same as ReelEditor
    const parentPath = (() => {
        if (location.state?.from) {
            const p = location.state.from;
            return p.startsWith('/') ? p.replace(/\/+$/, '') || '/' : `/${p}`.replace(/\/+$/, '') || '/';
        }
        try {
            const raw = localStorage.getItem('breadcrumbs');
            const parsed = JSON.parse(raw || '[]');
            if (Array.isArray(parsed) && parsed.length > 0) return `/${parsed.join('/')}`;
        } catch (_) {}
        return '/';
    })();

    const handleBreadcrumbNavigate = useCallback(
        (crumb) => {
            navigate('/', { state: { fromVideoEdit: true, folderPath: crumb?.path || '/', refreshNeeded: false } });
        },
        [navigate],
    );

    // Resizable split: monitorHeight is the pixel height of the program monitor pane
    const [monitorHeight, setMonitorHeight] = useState(null); // null = use default 60%
    const bodyRef = useRef(null);
    const dragging = useRef(false);

    const onDragHandleMouseDown = useCallback((e) => {
        e.preventDefault();
        dragging.current = true;
        const onMove = (ev) => {
            if (!dragging.current || !bodyRef.current) return;
            const rect = bodyRef.current.getBoundingClientRect();
            const newH = ev.clientY - rect.top;
            const min = 120;
            const max = rect.height - 120;
            setMonitorHeight(Math.max(min, Math.min(max, newH)));
        };
        const onUp = () => {
            dragging.current = false;
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    }, []);

    /* ── Load video data + subtitles ──────────────────────────────  */

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            setError(null);
            try {
                // Try route state first, then localStorage, then API
                let videoData =
                    location.state?.videoData ||
                    (() => {
                        try {
                            return JSON.parse(localStorage.getItem('videoEditorProps') || 'null');
                        } catch {
                            return null;
                        }
                    })();

                if (!videoData) {
                    const vid = params.videoId;
                    if (!vid) throw new Error('No video ID provided');
                    const userData = JSON.parse(localStorage.getItem('authUser') || '{}');
                    const resp = await videoCRUDServiceInstance.getVideoById(
                        vid,
                        userData.email,
                        userData.access,
                    );
                    videoData = resp.data;
                }

                // Prefer compressedUrl (same as the existing Player.js uses),
                // fall back to videoUrl or other known fields.
                const url =
                    videoData.compressedUrl ||
                    videoData.videoUrl ||
                    videoData.originalUrl ||
                    videoData.hlsUrl ||
                    '';

                setVideo(
                    videoData._id || videoData.id,
                    url,
                    videoData.videoName || videoData.title || 'Untitled',
                    videoData,
                );
                setCurrentVideoData(videoData);

                // If navigated from FolderBrowser with openDubSync=true, auto-open the dialog
                if (location.state?.openDubSync) {
                    setDubSyncDialogOpen(true);
                }

                // Restore previously saved editor state (cuts, music, logo)
                if (videoData.videoEditorState) {
                    restoreEditorState(videoData.videoEditorState);
                }

                // Load subtitle cues from VTT files
                await loadSubtitlesFromVideoData(videoData, loadSubtitles);

                // Restore existing DubSync tracks (from previous sessions).
                // Must run AFTER loadSubtitles so subtitle state is already populated.
                const allLangs = videoData.videoLanguages || [];
                for (const dsLang of allLangs.filter(l => l.languageName?.endsWith('-DubSync'))) {
                    const baseLanguage = dsLang.languageName.replace(/-DubSync$/, '');
                    const baseLangEntry = allLangs.find(l => l.languageName === baseLanguage);
                    const hasDubbedAudio = Boolean(baseLangEntry?.dubbingTransformedUrl);
                    addDubSyncTrack(baseLanguage, hasDubbedAudio);

                    // Load the saved timeline VTT so the track starts in SYNCED state,
                    // preventing the Manual toggle from re-bootstrapping on re-open.
                    if (dsLang.vttFileUrl) {
                        try {
                            const resp = await fetch(dsLang.vttFileUrl);
                            if (resp.ok) {
                                const text = await resp.text();
                                const cues = text.trimStart().startsWith('WEBVTT')
                                    ? parseVtt(text) : parseSrt(text);
                                if (cues.length > 0) {
                                    // setDubSyncCues sets trackState → SYNCED and populates subtitlesByLanguage
                                    setDubSyncCues(baseLanguage, cues, [], [], [], []);
                                }
                            }
                        } catch (e) {
                            console.warn('Failed to restore DubSync timeline for', baseLanguage, e);
                        }
                    }
                }
            } catch (err) {
                console.error('Video Editor init error:', err);
                setError(err.message || 'Failed to load video');
            } finally {
                setLoading(false);
            }
        };

        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* ── Save editor state to backend ────────────────────────────── */

    const isSavingRef = useRef(false);

    const handleSave = useCallback(async () => {
        if (!videoId) return;
        isSavingRef.current = true;
        setSaveStatus('saving');
        try {
            const userData = JSON.parse(localStorage.getItem('authUser') || '{}');
            const editorState = {
                cutMarkers: state.cutMarkers,
                cuts: state.cuts,
                // Exclude DubSync raw audio tracks — they are session-only (re-added by Sync Magic)
                audioTracks: state.audioTracks.filter((t) => !t.id?.startsWith('dubsync-raw-')),
                muteVideoAudio: state.muteVideoAudio,
                logoTracks: state.logoTracks,
                videoStartOffset: state.videoStartOffset,
                videoEndOffset: state.videoEndOffset,
                savedAt: new Date().toISOString(),
                savedBy: userData.email || '',
            };
            await videoCRUDServiceInstance.saveVideoEditorState(videoId, editorState);

            // Auto-flush any open DubSync edit session — user doesn't need to click "Done" first
            const pending = pendingDubSyncEditsRef.current;
            if (pending?.lang && pending.cues?.length > 0) {
                updateDubSyncCues(pending.lang, pending.cues);
            }

            // Also persist DubSync timelines for all SYNCED/RENDERED tracks
            const syncedTracks = (state.dubSyncTracks || []).filter(
                (t) => t.trackState === 'SYNCED' || t.trackState === 'RENDERED',
            );
            if (syncedTracks.length > 0) {
                await Promise.allSettled(
                    syncedTracks.map((t) => {
                        // Use pending cues if this is the language currently being edited
                        const cues = (pending?.lang === t.language && pending.cues?.length > 0)
                            ? pending.cues
                            : (state.subtitlesByLanguage[`${t.language}-DubSync`] || []);
                        if (cues.length === 0) return Promise.resolve();
                        const vttContent = buildVttFromCues(cues);
                        return videoCRUDServiceInstance.dubSyncSaveTimeline(videoId, t.language, vttContent)
                            .catch((err) => console.warn('DubSync save-timeline failed for', t.language, err));
                    }),
                );
            }

            markSaved();
            setSaveStatus('saved');
            setLastSavedAt(new Date());
        } catch (err) {
            console.error('Failed to save editor state:', err);
            setSaveStatus('error');
        } finally {
            isSavingRef.current = false;
        }
    }, [videoId, state.cutMarkers, state.cuts, state.audioTracks, state.muteVideoAudio, state.logoTracks, state.videoStartOffset, state.videoEndOffset, state.dubSyncTracks, state.subtitlesByLanguage, updateDubSyncCues, pendingDubSyncEditsRef]);

    /* ── Keyboard: Escape → back ─────────────────────────────────── */

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') navigate(-1);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [navigate]);

    /* ── Auto-save every 60 seconds ─────────────────────────────── */

    useEffect(() => {
        const id = setInterval(() => {
            if (!isSavingRef.current) handleSave();
        }, 60_000);
        return () => clearInterval(id);
    }, [handleSave]);

    /* ── Render ─────────────────────────────────────────────────── */

    if (loading) {
        return (
            <Root $dark={darkMode}>
                <PageHeader darkMode={darkMode} toggleDarkMode={toggleDarkMode} themeVariant={themeVariant} />
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        gap: 2,
                    }}
                >
                    <CircularProgress />
                    <Typography variant="body2" sx={{ color: '#888' }}>
                        Loading video editor…
                    </Typography>
                </Box>
            </Root>
        );
    }

    if (error) {
        return (
            <Root $dark={darkMode}>
                <PageHeader darkMode={darkMode} toggleDarkMode={toggleDarkMode} themeVariant={themeVariant} />
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        gap: 2,
                    }}
                >
                    <Typography color="error">{error}</Typography>
                    <Button variant="outlined" onClick={() => navigate(-1)}>
                        Back to Folder
                    </Button>
                </Box>
            </Root>
        );
    }

    const cutCount = state.cuts.length;

    return (
        <Root $dark={darkMode}>
            {/* App Header — same as Reel Editor */}
            <PageHeader darkMode={darkMode} toggleDarkMode={toggleDarkMode} themeVariant={themeVariant} />

            {/* Single top bar: breadcrumbs left | "Video Editor" center | chips + Export right */}
            <TopBar id="video-editor-topbar" $dark={darkMode}>
                <TopLeft>
                    <FolderBreadcrumbs
                        currentPath={parentPath}
                        onNavigate={handleBreadcrumbNavigate}
                        isDarkBackground={darkMode}
                        maxItems={6}
                    />
                </TopLeft>

                <TopCenter>
                    <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 700, letterSpacing: 1, color: darkMode ? '#64b5f6' : '#1565c0' }}
                    >
                        Video Editor
                    </Typography>
                </TopCenter>

                <TopRight>
                    {cutCount > 0 && (
                        <Chip
                            label={`${cutCount} cut${cutCount !== 1 ? 's' : ''}`}
                            size="small"
                            color="error"
                            variant="outlined"
                            sx={{ fontSize: 10 }}
                        />
                    )}
                    {state.past.length > state.savedPastLength && (
                        <Chip
                            label="Unsaved edits"
                            size="small"
                            color="warning"
                            variant="outlined"
                            sx={{ fontSize: 10 }}
                        />
                    )}
                    <Tooltip title={state.mediaLibraryOpen ? 'Close Editor Controls' : 'Open Editor Controls'}>
                        <Button
                            variant={state.mediaLibraryOpen ? 'contained' : 'outlined'}
                            color="secondary"
                            size="small"
                            startIcon={<PermMediaIcon />}
                            onClick={toggleMediaLibrary}
                            sx={{ textTransform: 'none' }}
                        >
                            Controls
                        </Button>
                    </Tooltip>
                    <Tooltip title={lastSavedAt ? `Last saved at ${lastSavedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}` : 'Save cuts, music, logo & DubSync timings to the cloud'}>
                        <Button
                            variant="outlined"
                            color="success"
                            size="small"
                            startIcon={<SaveIcon />}
                            onClick={handleSave}
                            disabled={saveStatus === 'saving'}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                            {saveStatus === 'saving' ? 'Saving…' : 'Save'}
                        </Button>
                    </Tooltip>
                    <Tooltip title="Add DubSync track — transcribe & align dubbed audio">
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setDubSyncDialogOpen(true)}
                            sx={{ textTransform: 'none', borderColor: '#7e57c2', color: '#b39ddb' }}
                        >
                            🎙 DubSync
                        </Button>
                    </Tooltip>
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<FileDownloadIcon />}
                        onClick={() => setExportOpen(true)}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Export
                    </Button>
                </TopRight>
            </TopBar>

            {/* Main layout: [left media panel | video monitor] on top, full-width timeline below */}
            <EditorBody ref={bodyRef}>
                <TopArea style={monitorHeight ? { height: monitorHeight } : { flex: '0 0 45%' }}>
                    <LeftPanel $open={state.mediaLibraryOpen} $dark={darkMode}>
                        <MediaLibraryPanel darkMode={darkMode} onClose={toggleMediaLibrary} />
                    </LeftPanel>
                    <VideoPanel id="video-editor-monitor">
                        <ProgramMonitor />
                    </VideoPanel>
                </TopArea>

                <DragHandle $dark={darkMode} onMouseDown={onDragHandleMouseDown} />

                <TimelinePane id="video-editor-timeline">
                    <TimelinePanel />
                </TimelinePane>
            </EditorBody>

            <ExportDialog open={exportOpen} onClose={() => setExportOpen(false)} />
            <AddDubSyncTrackDialog
                open={dubSyncDialogOpen}
                onClose={() => setDubSyncDialogOpen(false)}
                videoLanguages={currentVideoData?.videoLanguages || []}
                existingDubSyncLanguages={(state.dubSyncTracks || []).map((t) => t.language)}
                onAdd={({ language, uploadedFile }) => {
                    const langEntry = currentVideoData?.videoLanguages?.find(
                        (vl) => vl.languageName === language,
                    );
                    const remoteDubbedAudioUrl = langEntry?.dubbingTransformedUrl || null;
                    const hasRemoteDubbedAudio = Boolean(remoteDubbedAudioUrl);
                    // Treat a freshly uploaded file as available audio so the track becomes READY
                    const hasDubbedAudio = hasRemoteDubbedAudio || Boolean(uploadedFile);
                    try { console.info('DubSync: adding track', { videoId: videoId, language, hasDubbedAudio, uploadedFileName: uploadedFile?.name }); } catch(_){ }
                    addDubSyncTrack(language, hasDubbedAudio);
                    if (uploadedFile) {
                        // Store the File reference on the track so Sync Magic can send it
                        updateDubSyncTrack(language, { uploadedAudioFile: uploadedFile });
                    }
                    // If the language already has a dubbed audio URL, add it as a playable audio track immediately
                    if (remoteDubbedAudioUrl) {
                        const rawTrackId = `dubsync-raw-${language}`;
                        const alreadyAdded = (state.audioTracks || []).some((t) => t.id === rawTrackId);
                        if (!alreadyAdded) {
                            addAudioTrack({
                                id:            rawTrackId,
                                name:          `${language} (dubbed)`,
                                url:           remoteDubbedAudioUrl,
                                volume:        1,
                                offsetSeconds: 0,
                                muted:         false,
                            });
                        }
                    }
                }}
            />
            <HelpOverlay helpItems={videoEditorHelpItems} title="Video Editor Guide" buttonId="help-video-editor" />
            <Snackbar
                open={saveStatus === 'saved' || saveStatus === 'error'}
                autoHideDuration={3000}
                onClose={() => setSaveStatus(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSaveStatus(null)}
                    severity={saveStatus === 'saved' ? 'success' : 'error'}
                    sx={{ width: '100%' }}
                >
                    {saveStatus === 'saved' ? 'Editor state saved successfully.' : 'Failed to save. Please try again.'}
                </Alert>
            </Snackbar>
        </Root>
    );
}

/* ── Root export ─────────────────────────────────────────────────── */

export default function VideoEditorPage({ darkMode, toggleDarkMode, themeVariant }) {
    return (
        <EditorProvider>
            <EditorInner darkMode={darkMode} toggleDarkMode={toggleDarkMode} themeVariant={themeVariant} />
        </EditorProvider>
    );
}

/* ── Subtitle loading helper ─────────────────────────────────────── */

async function loadSubtitlesFromVideoData(videoData, loadSubtitles) {
    const subtitlesByLanguage = {};

    // API fields: languageName, vttFileUrl, srtFileUrl
    const langs = videoData.videoLanguages || [];

    for (const langEntry of langs) {
        const lang = langEntry.languageName;
        if (!lang) continue;
        // Skip DubSync tracks — they are audio-aligned copies, not subtitle tracks
        if (lang.endsWith('-DubSync')) continue;

        let cues = [];

        // Guard: if vttFileUrl was corrupted by a past bug (points to a DubSync timeline file),
        // skip it and fall through to level0AIVttFileUrl or the SRT fallback below.
        const vttUrl = langEntry.vttFileUrl && !langEntry.vttFileUrl.includes('dubsync_timeline_')
            ? langEntry.vttFileUrl
            : (langEntry.level0AIVttFileUrl || null);

        // Try fetching VTT file URL first
        if (vttUrl) {
            try {
                const resp = await fetch(vttUrl);
                if (resp.ok) {
                    const text = await resp.text();
                    cues = text.trimStart().startsWith('WEBVTT') ? parseVtt(text) : parseSrt(text);
                }
            } catch {
                // Unable to fetch VTT — try SRT below
            }
        }

        // Fall back to SRT file URL
        if (cues.length === 0 && langEntry.srtFileUrl) {
            try {
                const resp = await fetch(langEntry.srtFileUrl);
                if (resp.ok) {
                    const text = await resp.text();
                    cues = parseSrt(text);
                }
            } catch {
                // Unable to fetch SRT — skip this language
            }
        }

        if (cues.length > 0) {
            subtitlesByLanguage[lang] = cues;
        }
    }

    if (Object.keys(subtitlesByLanguage).length > 0) {
        loadSubtitles(subtitlesByLanguage);
    }
}
